import Toast, { TOAST_TYPES } from "@luminecence/composants/Toast"
import { onSnapshot } from "firebase/firestore"
import { css, html, LitElement } from "lit"

export default class PageMessage extends LitElement {
  static properties = { idConversation: { type: String }, messages: { type: Array, state: true }, participant: { type: String, state: true } }

  static styles = css`
  :host {
    display: flex;
    flex-direction: column;
    height: 100%;
    gap: 10px;
  }

    .auteur {
      font-weight: bold;
    }

    .date {
      font-size: 0.8em;
    }

    ul {
      list-style-type: none;
    }

    .messages {
      display: flex;
      flex-direction: column;
      gap: 10px;
      padding: 0 20px;
    }

    .messages li {
      padding: 10px;
      border-radius: 8px;
      max-width: 50%;
    }

    .contenu {
      white-space: pre-wrap;
    }

    .messages li.autreParticipant {
      align-self: flex-end;
      background-color: var(--couleur-600);
    }

    .messages li.moi {
      background-color: var(--couleur-400);
    }

    .envoi-message {
      display: flex;
      flex-direction: row;
      gap: 10px;
      align-items: center;
      padding: 0 20px;
    }

    textarea {
        flex: 1;
        box-sizing: border-box;
        padding: 10px;
        font-size: 1.8rem;
        border-radius: var(--border-radius-champ);
        color: var(--couleur-texte);
        border: none;
        box-shadow: 2px 1px 15px #aaaaaa;
        padding: 0.7rem 0.7rem 0.7rem 1.5rem;
        background: var(--couleur-100);
    }

    textarea:focus {
        outline: var(--outline-input);
    }

    p-bouton-icone {
      --bouton-icone-background: transparent;
      --couleur-texte: var(--couleur-500);
    }
  `

  constructor() {
    super()
    this.idConversation = ""
    this.idParticipant = ""
    this.participant = ""
    this.messages = []
    this.callbackSnapshot = null
    this.estChargementInitial = true
  }

  async connectedCallback() {
    super.connectedCallback()
    await this.recupererMessages()
    this.callbackSnapshot = onSnapshot(await window.authentification.bdd.conversation.recupererLaReferenceDuneConversation(this.idConversation), async () => {
      await this.recupererMessages()
      if (!this.estChargementInitial && this.messages[this.messages.length - 1].auteurId !== window.authentification.getUser().uid) {
        Toast.afficher("Nouveau message reçu !", TOAST_TYPES.INFO)
      }
      this.estChargementInitial = false
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    if (this.callbackSnapshot) {
      this.callbackSnapshot()
    }
  }

  async recupererMessages() {
    const conversations = await window.authentification.bdd.conversation.recupererToutesLesConversationsDuMembreConnecte()
    const conversation = conversations.find((conv) => conv.id === this.idConversation)
    this.idParticipant = conversation.participants.find((participant) => participant !== window.authentification.getUser().uid)
    this.participant = await window.authentification.bdd.membres.recupererLePseudoDunMembre(this.idParticipant)
    const messages = await window.authentification.bdd.conversation.recupererTousLesMessagesDuneConversation(this.idConversation)
    const utilisateurConnecte = window.authentification.getUser().uid

    this.messages = await Promise.all(
      messages.map(async (message) => {
        const auteurPseudo =
          message.auteurId === utilisateurConnecte ? "Vous" : await window.authentification.bdd.membres.recupererLePseudoDunMembre(message.auteurId)

        return { ...message, auteurPseudo }
      }),
    )
  }

  render() {
    return html`
            <h1>Discussion avec ${this.participant}</h1>
            <ul class="messages">
                ${this.messages.map(
                  (message) => html`<li class="${message.auteurId === window.authentification.getUser().uid ? "moi" : "autreParticipant"}">
                    <div>
                    <span class="auteur">${message.auteurPseudo ?? "..."}</span>
                        <span class="date">${this.formaterDate(message.creerA)}</span>
                    </div>
                <div class="contenu">${message.contenu}</div></li>`,
                )}
            </ul>
            <div class="envoi-message">
              <textarea placeholder="Votre message..."></textarea>
              <p-bouton-icone id="envoyer" icone="envoyer" @click=${this.gererEnvoiMessage}></p-bouton-icone>
            </div>
        `
  }

  gererEnvoiMessage() {
    const texteArea = this.shadowRoot.querySelector("textarea")
    const contenu = texteArea.value
    if (contenu.trim() !== "") {
      const nouveauMessage = {
        auteurId: window.authentification.getUser().uid,
        contenu,
      }
      window.authentification.bdd.conversation.ajouterUnMessageDansUneConversation(this.idConversation, nouveauMessage)
      this.messages = [...this.messages, nouveauMessage]
      texteArea.value = ""
      this.notifierMessage(this.idParticipant)
    }
  }

  formaterDate(date) {
    if (!date) {
      return "À l'instant"
    }

    /** @type {Intl.DateTimeFormatOptions} */
    const options = { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }
    const dateJs = date?.toDate?.() ?? date
    return new Date(dateJs).toLocaleDateString("fr-FR", options)
  }

  async notifierMessage(idMembre) {
    const tokenDeAutreMembre = await window.authentification.bdd.membres.recupererLeTokenDeNotificationDuMembre(idMembre)

    if (!tokenDeAutreMembre) {
      console.warn("Aucun token FCM pour ce membre, notification ignoree")
      return
    }

    const reponse = await fetch("/.netlify/functions/envoyerNotif", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        token: tokenDeAutreMembre,
        titre: "Nouveau message",
        message: `Tu as reçu un nouveau message de ${this.participant}`,
        url: `/conversations/${this.idConversation}`,
      }),
    })

    if (!reponse.ok) {
      const erreur = await reponse.text()
      console.error("Echec envoi notification", erreur)
    }
  }
}

customElements.define("p-page-message", PageMessage)
